import { ReactNode, useEffect, useState } from "react";
import { Field, Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import * as Yup from "yup";

import { useAppSelector } from "../../shared/hook";
import Loader from "../../shared/Ui/Loader/Loader";
import StatusBadge from "../../shared/Ui/StatusBadge/StatusBadge";
import ModalComponent from "../../shared/Ui/ModalComponent/ModalWindow";
import ModalButton from "../../shared/Ui/Buttons/ModalButtons";
import AlertBox from "../../shared/Ui/AlertBox/AlertBox";
import CalendarSelectFormik from "../../shared/Ui/Calendar/CalendarSelectFormik";
import AccountOperationsReport from "./AccountOperationsReport";
import profileStyle from "../ProfileCard/ProfileCard.module.css";
import {
  AccountCard,
  getAccountById,
  saveKtAccount,
  topUpAccount,
} from "../../common/api/billingApi";
import PageNotRole from "../../shared/Ui/PageNotFound/PageNotRole";
import { canAccessLocalBilling, formatBillingAmount } from "./billingUtils";

const formatCurrency = formatBillingAmount;

const ReadOnlyField = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) => (
  <div className="w-full md:w-[calc(50%-8px)]">
    <label className="text-sm text-gray-500 block mb-1">{label}</label>
    <input
      type="text"
      readOnly
      value={value ?? "-"}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-sm"
    />
  </div>
);

// Строка карточки в стиле чека: подпись слева, значение справа
const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value?: string | number | null;
}) => (
  <div className="flex items-baseline justify-between gap-6 py-2 border-b border-dashed border-gray-200 last:border-b-0">
    <span className="text-sm text-gray-600 shrink-0">{label}</span>
    <span className="font-medium text-gray-900 text-right break-words min-w-0">
      {value || value === 0 ? value : "-"}
    </span>
  </div>
);

const InfoSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div>
    <h3 className={`${profileStyle.orgTitleMain} text-red-700 pb-2 border-b border-gray-300`}>
      {title}
    </h3>
    <div className="flex flex-col">{children}</div>
  </div>
);

const FieldError = ({
  name,
  errors,
  touched,
  submitCount,
}: {
  name: string;
  errors: Record<string, unknown>;
  touched: Record<string, unknown>;
  submitCount: number;
}) => {
  const error = errors[name];
  if (!(touched[name] || submitCount > 0) || !error) return null;
  return <p className="text-red-500 text-xs mt-1">{String(error)}</p>;
};

function AccountDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { accountId } = useParams();
  const userData: any = useAppSelector((state) => state.auth.userData);
  const hasLocalBillingAccess = canAccessLocalBilling(userData);

  const [account, setAccount] = useState<AccountCard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reportReloadKey, setReportReloadKey] = useState(0);
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [ktAccount, setKtAccount] = useState("");
  const [isSavingKt, setIsSavingKt] = useState(false);
  const [topUpError, setTopUpError] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState<any>(null);

  const loadAccount = async (silent = false) => {
    if (!accountId) return;
    if (!silent) setIsLoading(true);
    try {
      const data = await getAccountById(Number(accountId));
      setAccount(data);
      setKtAccount(data.ktAccount ?? "");
    } catch (error) {
      console.error("Error fetching account:", error);
      setAccount(null);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!hasLocalBillingAccess) return;
    loadAccount();
  }, [accountId, hasLocalBillingAccess]);

  const handleSaveKtAccount = async () => {
    if (!accountId || !ktAccount.trim()) return;
    setIsSavingKt(true);
    try {
      await saveKtAccount(Number(accountId), { ktAccount: ktAccount.trim() });
      await loadAccount(true);
    } catch (error) {
      console.error("Error saving KT account:", error);
    } finally {
      setIsSavingKt(false);
    }
  };

  const topUpValidation = Yup.object({
    paymentDate: Yup.string().trim().required(t("billing.requiredField")),
    amount: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" || originalValue === null ? undefined : value,
      )
      .typeError(t("billing.requiredField"))
      .required(t("billing.requiredField")),
    paymentOrder: Yup.string().trim().required(t("billing.requiredField")),
    payerData: Yup.string()
      .trim()
      .required(t("billing.requiredField"))
      .min(12, t("billing.payerDataMinLength"))
      .max(128, t("billing.payerDataMaxLength")),
  });

  const formatDateForApi = (value: string) => {
    if (!value) return "";
    const parts = value.split(".");
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return value;
  };

  if (!hasLocalBillingAccess) {
    return <PageNotRole />;
  }

  if (isLoading) {
    return <Loader />;
  }

  if (!account) {
    return (
      <div className="p-4">
        <p className="text-gray-600">{t("billing.noData")}</p>
        <button
          type="button"
          className="text-green underline mt-2"
          onClick={() => navigate("/billing/accounts")}
        >
          {t("billing.backToJournal")}
        </button>
      </div>
    );
  }

  const openedAtLabel = account.openedAt
    ? format(new Date(account.openedAt), "dd.MM.yyyy")
    : "-";

  const contractDateLabel = account.eoContractDate
    ? format(new Date(account.eoContractDate), "dd.MM.yyyy")
    : "-";

  const { building, premises } = account.address ?? {};
  const buildingPremisesLabel =
    building || premises ? `${building || "-"} / ${premises || "-"}` : "-";

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-4 my-3">
        <div>
          <h2 className="text-xl font-semibold">
            {t("billing.accountCardTitle")} {account.accountNumber}{" "}
            {t("billing.fromDate", { date: openedAtLabel })}
          </h2>
          <div className="mt-2">
            <StatusBadge type={account.status === "ACTIVE" ? "active" : undefined}>
              {t("billing.statusLabel")}:{" "}
              {account.status === "ACTIVE"
                ? t("billing.statusActive")
                : account.status}
            </StatusBadge>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="bg-green text-white font-semibold py-2 px-4 rounded"
            onClick={() => setIsTopUpOpen(true)}
          >
            {t("billing.topUpAccount")}
          </button>
          <button
            type="button"
            className="text-gray-600 font-semibold py-2 px-4"
            onClick={() => navigate("/billing/accounts")}
          >
            {t("billing.close")}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md px-6 py-6">
        <div className="pb-5 border-b border-dashed border-gray-300">
          <div className="text-sm text-gray-600">{t("billing.currentBalance")}</div>
          <div className="text-[28px] leading-tight font-semibold text-green">
            {formatCurrency(account.currentBalance)}
          </div>
        </div>

        {/* Сетка, а не CSS columns: разделы одного ряда начинаются на одном уровне.
            1 колонка → 2 (lg) → 3 (от 1440px) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 min-[1440px]:grid-cols-3 gap-x-10 gap-y-6 mt-5">
          <InfoSection title={t("billing.organizationData")}>
            <InfoRow label={t("billing.orgIinBin")} value={account.orgIinBin} />
            <InfoRow label={t("billing.orgName")} value={account.orgName} />
            <InfoRow
              label={t("billing.organizationEmail")}
              value={account.organizationEmail}
            />
          </InfoSection>

          <InfoSection title={t("billing.addressData")}>
            <InfoRow label={t("billing.city")} value={account.address?.city} />
            <InfoRow label={t("billing.street")} value={account.address?.street} />
            <InfoRow label={t("billing.house")} value={account.address?.house} />
            <InfoRow
              label={`${t("billing.building")} / ${t("billing.premises")}`}
              value={buildingPremisesLabel}
            />
          </InfoSection>

          <InfoSection title={t("billing.bankData")}>
            <InfoRow label={t("billing.bankName")} value={account.bank?.bankName} />
            <InfoRow label={t("billing.bic")} value={account.bank?.bankIdentificationCode} />
            <InfoRow label={t("billing.kbe")} value={account.bank?.kbe} />
            <InfoRow
              label={t("billing.checkingAccount")}
              value={account.bank?.checkingAccount}
            />
          </InfoSection>

          <InfoSection title={t("billing.contractInfo")}>
            <InfoRow
              label={t("billing.contractNumber")}
              value={account.eoContractNumber}
            />
            <InfoRow label={t("billing.contractDate")} value={contractDateLabel} />
            <InfoRow
              label={t("billing.productGroup")}
              value={account.productGroupName}
            />
          </InfoSection>

          <InfoSection title={t("billing.additionalInfo")}>
            <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 py-2">
              <span className="text-sm text-gray-600 shrink-0">
                {t("billing.ktAccount")}
              </span>
              <div className="flex gap-2 flex-1 min-w-[220px] max-w-[360px]">
                <input
                  type="text"
                  value={ktAccount}
                  onChange={(e) => setKtAccount(e.target.value)}
                  placeholder="KT1234567"
                  className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  className="bg-green text-white font-semibold py-2 px-4 rounded whitespace-nowrap"
                  disabled={isSavingKt || ktAccount === (account.ktAccount ?? "")}
                  onClick={handleSaveKtAccount}
                >
                  {t("billing.save")}
                </button>
              </div>
            </div>
          </InfoSection>
        </div>
      </div>

      <h3 className="font-medium text-[20px] mt-6 mb-3">{t("billing.monthlyReport")}</h3>
      <AccountOperationsReport
        accountId={Number(accountId)}
        account={account}
        reloadKey={reportReloadKey}
      />

      {isTopUpOpen && (
        <ModalComponent
          isOpen={isTopUpOpen}
          onClose={() => {
            setIsTopUpOpen(false);
            setTopUpError(null);
          }}
          headerContent={
            <h3 className="text-lg font-semibold uppercase text-gray-500">
              {t("billing.topUpOperationTitle")}
            </h3>
          }
          customContent={
            <Formik
              key={`topup-${accountId}`}
              initialValues={{
                paymentDate: format(new Date(), "dd.MM.yyyy"),
                amount: "",
                paymentOrder: "",
                payerData: account.orgIinBin ?? "",
              }}
              validationSchema={topUpValidation}
              validateOnBlur
              validateOnChange
              onSubmit={async (values, { setSubmitting, resetForm }) => {
                setTopUpError(null);
                try {
                  await topUpAccount(Number(accountId), {
                    amount: Number(values.amount),
                    paymentDate: formatDateForApi(values.paymentDate),
                    paymentOrder: values.paymentOrder.trim(),
                    payerData: values.payerData.trim(),
                  });
                  await loadAccount(true);
                  setReportReloadKey((key) => key + 1);
                  resetForm();
                  setIsTopUpOpen(false);
                  setAlertOpen({
                    status: "SUCCESS",
                    message: t("billing.topUpSuccess"),
                  });
                } catch {
                  setTopUpError(t("billing.topUpError"));
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({ errors, touched, submitCount, isSubmitting }) => (
                <Form className="p-4 min-w-[500px]">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800">
                    {t("billing.topUpWarning")}
                  </div>

                  <ReadOnlyField
                    label={t("billing.accountNumber")}
                    value={account.accountNumber}
                  />
                  <div className="h-3" />
                  <ReadOnlyField label={t("billing.orgName")} value={account.orgName} />

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-500 block mb-1">
                        {t("billing.paymentDate")} *
                      </label>
                      <CalendarSelectFormik
                        name="paymentDate"
                        allowFuture
                        documentStatus="new"
                      />
                      <FieldError
                        name="paymentDate"
                        errors={errors}
                        touched={touched}
                        submitCount={submitCount}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-500 block mb-1">
                        {t("billing.topUpAmount")} *
                      </label>
                      <Field
                        type="number"
                        name="amount"
                        step="0.01"
                        placeholder={t("billing.topUpAmountPlaceholder")}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                      <FieldError
                        name="amount"
                        errors={errors}
                        touched={touched}
                        submitCount={submitCount}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {t("billing.topUpAmountHint")}
                      </p>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm text-gray-500 block mb-1">
                        {t("billing.paymentOrder")} *
                      </label>
                      <Field
                        type="text"
                        name="paymentOrder"
                        placeholder={t("billing.paymentOrderPlaceholder")}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                      <FieldError
                        name="paymentOrder"
                        errors={errors}
                        touched={touched}
                        submitCount={submitCount}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-sm text-gray-500 block mb-1">
                        {t("billing.payerData")} *
                      </label>
                      <Field
                        type="text"
                        name="payerData"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                      />
                      <FieldError
                        name="payerData"
                        errors={errors}
                        touched={touched}
                        submitCount={submitCount}
                      />
                    </div>
                  </div>

                  {topUpError && (
                    <p className="text-red-500 text-sm mt-3">{topUpError}</p>
                  )}

                  <div className="flex justify-end gap-3 mt-6">
                    <ModalButton
                      type="button"
                      onClick={() => {
                        setIsTopUpOpen(false);
                        setTopUpError(null);
                      }}
                    >
                      {t("billing.cancel")}
                    </ModalButton>
                    <ModalButton
                      color="green"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {t("billing.conductOperation")}
                    </ModalButton>
                  </div>
                </Form>
              )}
            </Formik>
          }
        />
      )}

      <AlertBox isOpen={alertOpen} setIsOpen={setAlertOpen} />
    </div>
  );
}

export default AccountDetails;
